import React, { useState, useEffect } from "react";
import { Typography, Box, IconButton, CircularProgress } from "@mui/material";
import { useBookmakers } from "../contexts/BookmakersContext";
import BetTrackerButton from "./BetTrackerButton";
import { formatRate, getBookieLogoUrl } from "../constants";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";

const GroupedBetDetails = ({ bet, market, onBack }) => {
  const { bookmakers, selectedBookmakers } = useBookmakers();
  const isPro = market.level === "Pro";
  const [detailedBets, setDetailedBets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetailedBets = async () => {
      setLoading(true);
      try {
        let url;
        if (window.location.hostname === "localhost") {
          url = `/mock-grouped-bet-details.json`;
        } else {
          url = `${window.location.origin}/wp-json/bonusbank/v1/event/${market.eventId}/?markets=${market.marketId}&grouped_bet_id=${bet.value.id}`;
        }
        const response = await axios.get(url, {
          headers: {
            "content-type": "application/json",
            "X-WP-Nonce": window.nonce,
          },
        });
        setDetailedBets(response.data);
        setError(null);
      } catch (err) {
        setError(err);
        setDetailedBets(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedBets();
  }, [bet.value.id, market.eventId, market.marketId]);

  const renderBookieLogo = (bookie) => (
    <Box key={bookie} sx={{ flexGrow: 1, height: "40px", marginRight: "12px" }}>
      <img
        src={getBookieLogoUrl(bookie)}
        alt={bookie}
        style={{
          borderRadius: "12px",
          width: "100%",
          height: "40px",
          objectFit: "cover",
        }}
      />
    </Box>
  );

  const renderChip = (
    label,
    value,
    highlight = false,
    isPercentage = false
  ) => {
    if (value === 0 || value === null || value === undefined) return null;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginRight: "16px",
        }}
      >
        <Typography variant="caption">{label}</Typography>
        <Typography
          variant="body2"
          sx={{
            flexShrink: 0,
            borderRadius: "6px",
            width: "60px",
            height: "30px",
            backgroundColor: (theme) =>
              highlight ? "#35BD5B" : theme.palette.eventBackground.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "500",
            fontSize: "14px",
          }}
        >
          {isPercentage
            ? `${((value - 1) * 100).toFixed(0)}%`
            : formatRate(value)}
        </Typography>
      </Box>
    );
  };

  const renderBookieChip = (betItem) => {
    const betTrackerContent = `${market.date.toISOString().slice(0, 10)}\t${
      betItem.bookie === "Neds Group" ? "Neds" : betItem.bookie
    }\tMug\t${localStorage.getItem("odds-comparison-sport") || "Soccer"}\t${
      market.eventName
    }\t${bet.key}\t\t${betItem.rate}`;

    const showEVIndicator = isPro && betItem.ev >= 3;

    return (
      <Box
        key={betItem.bookie}
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            flexGrow: 1,
          }}
          onClick={() => window.open(betItem.source, "_blank")}
        >
          {renderBookieLogo(betItem.bookie)}
        </Box>
        <BetTrackerButton content={betTrackerContent} />
        <Typography
          variant="body2"
          sx={{
            flexShrink: 0,
            borderRadius: "6px",
            width: "60px",
            height: "30px",
            backgroundColor: (theme) =>
              formatRate(bet.value.maxOdds) === formatRate(betItem.rate)
                ? "#35BD5B"
                : theme.palette.eventBackground.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "500",
            position: "relative",
            marginLeft: "12px",
            fontSize: "14px",
          }}
        >
          {formatRate(betItem.rate)}
          {showEVIndicator && (
            <Box
              sx={{
                position: "absolute",
                top: "2px",
                right: "2px",
                width: "8px",
                height: "8px",
              }}
            >
              <AttachMoneyIcon sx={{ fontSize: "8px", color: "#FFFFFF" }} />
            </Box>
          )}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">Error loading bet details</Typography>;
  }

  if (!detailedBets) {
    return <Typography>No bet details available</Typography>;
  }

  const sortedBets = detailedBets.bets.sort((a, b) => b.rate - a.rate);
  const selectedBets = sortedBets.filter((bet) =>
    selectedBookmakers.includes(bet.bookie)
  );
  const unselectedBets = sortedBets.filter(
    (bet) => !selectedBookmakers.includes(bet.bookie)
  );

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          zIndex: 1,
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body1">Back</Typography>
        </Box>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: "600" }}>
          {bet.key}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", mb: 2 }}>
          {renderChip("Best Odds", bet.value.maxOdds, true)}
          {renderChip("Avg Odds", bet.value.aveOdds)}
          {renderChip("Best / Avg", bet.value.maxAveRatio, false, true)}
          {bet.value.fairOdds > 0 &&
            renderChip("Fair Odds", bet.value.fairOdds)}
        </Box>
      </Box>

      <Box>
        {selectedBets.length > 0 ? (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Selected Bookmakers:
            </Typography>
            <Box sx={{ mb: 2 }}>{selectedBets.map(renderBookieChip)}</Box>

            <Typography variant="body1" sx={{ mb: 1 }}>
              Other Bookmakers:
            </Typography>
            <Box>{unselectedBets.map(renderBookieChip)}</Box>
          </>
        ) : (
          <Box>{sortedBets.map(renderBookieChip)}</Box>
        )}
      </Box>
    </Box>
  );
};

export default GroupedBetDetails;
